import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';

const STORAGE_KEY = 'educators-pinned-features';
const PIN_LIMIT = 3;

// One entry per user, the way every other per-user preference on this device is
// stored — see src/utils/tourGuide.js and the welcome popup key in ../index.jsx.
const getStorageKey = (userId) => `${STORAGE_KEY}_${userId}`;

// The stored array is already in display order — newest pin first — so there is
// nothing to sort and no timestamp to keep.
//
// Nothing about localStorage is trustworthy: it can be hand-edited, and it can
// hold keys from an older release or from a tool the account has since lost.
// Anything that is not a known key is dropped rather than allowed to occupy one
// of the three slots invisibly.
const readPinnedKeys = (userId, knownKeys) => {
  if (!userId) return [];

  let stored;
  try {
    stored = JSON.parse(localStorage.getItem(getStorageKey(userId)));
  } catch {
    return [];
  }
  if (!Array.isArray(stored)) return [];

  return stored
    .filter(
      (key, index) => knownKeys.includes(key) && stored.indexOf(key) === index,
    )
    .slice(0, PIN_LIMIT);
};

const writePinnedKeys = (userId, keys) => {
  if (!userId) return;

  localStorage.setItem(getStorageKey(userId), JSON.stringify(keys));
};

/**
 * Pinned feature keys for the signed-in user, newest pin first.
 *
 * `featureKeys` is the keys of the cards actually on screen, already filtered by
 * feature flag, so a pin for a tool the account cannot use never survives.
 */
const useFeaturePins = (featureKeys) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user?.id);

  const [pinnedKeys, setPinnedKeys] = useState(() =>
    readPinnedKeys(userId, featureKeys),
  );

  // `featureKeys` is a fresh array on every render, so the effect is keyed on its
  // contents instead of its identity.
  const featureKeysSignature = featureKeys.join(',');

  // Two things arrive after the first render and both change what can be read:
  // the store starts with an empty user and fills in on VERIFY_TOKEN_SUCCESS, and
  // the feature flags that decide which cards exist resolve asynchronously. Every
  // change to `pinnedKeys` is written straight to storage, so re-reading is
  // always consistent — and a pin hidden by a flag comes back if the flag does.
  useEffect(() => {
    setPinnedKeys(readPinnedKeys(userId, featureKeysSignature.split(',')));
  }, [userId, featureKeysSignature]);

  const togglePin = useCallback(
    (key) => {
      if (pinnedKeys.includes(key)) {
        const next = pinnedKeys.filter((item) => item !== key);
        setPinnedKeys(next);
        writePinnedKeys(userId, next);
        return;
      }

      if (pinnedKeys.length >= PIN_LIMIT) {
        // The message carries the cap, which NotiHandler's own t() call cannot
        // interpolate, so it is translated here — same as the favourite-voices
        // limit toast in MobileTTS/NewVoiceDrawer/VoiceDrawer.jsx.
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: t('educatorsFeaturePinLimit', { number: PIN_LIMIT }),
          }),
        );
        return;
      }

      const next = [key, ...pinnedKeys];
      setPinnedKeys(next);
      writePinnedKeys(userId, next);
    },
    [pinnedKeys, userId, dispatch, t],
  );

  return { pinnedKeys, togglePin };
};

export default useFeaturePins;
